import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { createStyles, Fade, makeStyles } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import { Loader } from '@googlemaps/js-api-loader'
import Geosuggest from 'react-geosuggest'
import CMSText from './CMSText'
import { default as Loading } from '../Loader/Loader'

const loader = new Loader({
  apiKey: process.env.GMAP_API_KEY,
  version: 'weekly',
  libraries: ['places'],
})

const MIN_LENGHT_SEARCH = 5
const excludedPlaces = process.env.EXCLUDED_SHIPPING_PLACES

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      position: 'relative',
      '& .geosuggest': {
        position: 'relative',
        '&__input': {
          display: 'block',
          width: '100%',
          margin: 'auto',
          padding: theme.spacing(1, 1.25),
          fontFamily: theme.typography.fontFamily,
          fontSize: 14,
          borderRadius: 6,
          border: `1px solid ${theme.extraColors.greyLight}`,
          color: theme.typography.body1.color,
          backgroundColor: theme.extraColors.greyLight,
          WebkitAppearance: 'none',
          '&::placeholder': {
            opacity: 0.75,
            color: theme.typography.body1.color,
          },
          '&:disabled': {
            userSelect: 'none',
            cursor: 'not-allowed',
          },
        },
        '&__suggests': {
          listStyle: 'none',
          margin: 0,
          padding: 0,
          left: 0,
          right: 0,
          top: '100%',
          zIndex: theme.zIndex.appBar,
          background: theme.palette.common.white,
          boxShadow: theme.shadows[1],
          borderRadius: theme.shape.borderRadius,
          '&--hidden': {
            display: 'none',
          },
          '&-wrapper': {},
        },
        '&__item': {
          padding: theme.spacing(1, 1.25),
          cursor: 'pointer',
          borderBottom: `1px solid ${theme.extraColors.greyLight}`,
          '&:hover, &:focus, &--active': {
            background: theme.extraColors.greyLight,
          },
          '&:last-child': {
            border: 0,
          },
        },
      },
    },
    label: {
      display: 'block',
      cursor: 'pointer',
      textAlign: 'inherit',
      fontSize: 11,
      marginLeft: theme.spacing(1.25),
      marginBottom: theme.spacing(0.5),
    },
    rootLoading: {
      position: 'relative',
      minHeight: '3em',
    },
    errorContainer: {
      minHeight: '2em',
      marginTop: theme.spacing(1),
    },
    error: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      '& p': {
        margin: 0,
      },
    },
    errorColor: {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
    overlay: {
      position: 'absolute',
      zIndex: theme.zIndex.appBar,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'rgba(255,255,255, .5)',
      backdropFilter: 'blur(2px)',
    },
  })
)

type TProps = {
  parameters: any
  datas: any
  label: any
  placeholder: any
  saveSearchAddress?: any
  changeZipCode?: any
  carrierId?: any
  type?: any
  value?: any
}

const mapStateToProps = (state) => ({
  parameters: state.parameters,
})

const GoogleMapsApi = ({
  parameters,
  datas,
  label,
  placeholder,
  saveSearchAddress,
  changeZipCode,
  carrierId,
  type,
  value,
}: TProps) => {
  const { t } = useTranslation()
  const countries = parameters?.countries
  const classes = useStyles()
  const [searchReady, setSearchReady] = useState<boolean>(false)
  const [noResults, setNoResults] = useState<boolean>(false)
  const [areaError, setAreaError] = useState<boolean>(false)
  const [countryError, setCountryError] = useState<boolean>(false)
  const [searchError, setSearchError] = useState<boolean>(false)
  // TODO change this to configuration with env var or parmameter to use it with other stores
  const noPostalCode = process.env.STORE === 'chalhoub'
  const countryCodes = []
  countries?.forEach((country) => countryCodes.push(country.code.toLowerCase()))

  const handleSelect = (suggest) => {
    setNoResults(false)
    let countryCode
    let country
    let postalcode
    let city
    let streetnumber
    let street
    let area
    let areaCode
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let details
    // First fill street with suggest.gmaps.name for not revelant adresses (ex: allée de la Cascade, Arnas, France)
    street = suggest?.gmaps?.name
    // Second go through each address_component types and fill each vars.
    // street will be redefined if type 'route' exist (ex: 15 rue de milan)
    suggest?.gmaps.address_components.forEach((address_component) => {
      address_component.types.forEach((type) => {
        if (type === 'country') {
          countryCode = address_component.short_name
          country =
            countries.find((country) => country.code === countryCode)?.name ||
            address_component.long_name
        }
        if (type === 'postal_code') {
          postalcode = address_component.long_name
        }
        if (type === 'locality' || type === 'postal_town') {
          city = address_component.long_name
        }
        if (type === 'sublocality_level_1') {
          details = address_component.long_name
        }
        if (type === 'street_number') {
          streetnumber = address_component.long_name
        }
        if (type === 'route') {
          street = address_component.long_name
        }
        if (type === 'administrative_area_level_1') {
          area = address_component.long_name
          areaCode = address_component.short_name
        }
      })
    })
    // if (details) {
    //   street = street + ' - ' + details
    // }
    const isValidCountry = countryCodes.includes(
      countryCode && countryCode.toLowerCase()
    )

    const handleAddressDetail = () => {
      const addressDetail = {
        hasHouseNumber: !!streetnumber,
        streetNumber: streetnumber,
        street: street,
        city: city,
        area,
        areaCode,
        postcode: postalcode,
        country_code: countryCode,
        country: country,
      }
      changeZipCode &&
        changeZipCode(city, postalcode, carrierId, countryCode, type, street)
      saveSearchAddress && saveSearchAddress(addressDetail)
    }

    const isExcludedArea =
      excludedPlaces && excludedPlaces.split(',').includes(area && area)
    if (noPostalCode) {
      postalcode = '-'
    }
    const isAddressValid =
      (countryCode !== 'AE' && saveSearchAddress && street && postalcode) ||
      (changeZipCode && city && postalcode) ||
      (countryCode === 'AE' && saveSearchAddress && street) ||
      (changeZipCode && city)
    // First check if result is in a valid country
    if (isValidCountry) {
      setCountryError(false)
      // Second check if result is in an excluded area
      if (isExcludedArea) {
        setAreaError(true)
      } else {
        setAreaError(false)
        if (isAddressValid) {
          handleAddressDetail()
          setSearchError(false)
        } else {
          setSearchError(true)
        }
      }
    } else {
      setCountryError(true)
    }
  }
  const handleNoResults = (userInput) => {
    if (userInput.length > MIN_LENGHT_SEARCH) {
      setNoResults(true)
    }
    setCountryError(false)
    setSearchError(false)
    setAreaError(false)
  }

  useEffect(() => {
    loader
      .load()
      .then(() => setSearchReady(true))
      .catch(() => setSearchReady(false))
  }, [searchReady])

  return (
    <>
      {searchReady ? (
        <div className={clsx('searchApp', classes.root)}>
          <label
            htmlFor={'geosuggest__input'}
            className={clsx('label', classes.label)}
          >
            {label}
          </label>
          <Geosuggest
            inputType={'search'}
            minLength={MIN_LENGHT_SEARCH}
            placeholder={placeholder}
            onSuggestSelect={handleSelect}
            initialValue={value}
            onSuggestNoResults={handleNoResults}
            onUpdateSuggests={() => setNoResults(false)}
            country={countryCodes?.length > 5 ? null : countryCodes}
          />
          <div className={classes.errorContainer}>
            {noResults && (
              <Fade in>
                <div
                  className={classes.error}
                  data-searchstate="search-noresults"
                >
                  <ErrorIcon color={'error'} />
                  <div className={classes.errorColor}>
                    <CMSText
                      data={datas?.no_results}
                      defaultText={t('texts:general:search_address:no_results')}
                    />
                  </div>
                </div>
              </Fade>
            )}
            {areaError && (
              <Fade in>
                <div
                  className={classes.error}
                  data-searchstate="search-invalid-country"
                >
                  <ErrorIcon color={'error'} />
                  <div className={classes.errorColor}>
                    <CMSText
                      data={datas?.excluded}
                      defaultText={t('texts:general:search_address:excluded')}
                    />
                  </div>
                </div>
              </Fade>
            )}
            {countryError && (
              <Fade in>
                <div
                  className={classes.error}
                  data-searchstate="search-invalid-country"
                >
                  <ErrorIcon color={'error'} />
                  <div className={classes.errorColor}>
                    <CMSText
                      data={datas?.invalid_country}
                      defaultText={t(
                        'texts:general:search_address:invalid_country'
                      )}
                    />
                  </div>
                </div>
              </Fade>
            )}
            {searchError && (
              <Fade in>
                <div
                  className={classes.error}
                  data-searchstate="search-incomplete"
                >
                  <ErrorIcon color={'error'} />
                  <div className={classes.errorColor}>
                    <CMSText
                      data={datas?.incomplete}
                      defaultText={t('texts:general:search_address:incomplete')}
                    />
                  </div>
                </div>
              </Fade>
            )}
          </div>
        </div>
      ) : (
        <div className={clsx('searchApp', classes.rootLoading)}>
          <div className={classes.overlay}>
            <Loading />
          </div>
        </div>
      )}
    </>
  )
}

export default connect(mapStateToProps)(GoogleMapsApi)
